import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Users, Lock, Unlock, Search, Loader2, ArrowLeft, Send, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GroupsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', is_private: false });

  // Group detail view
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupPosts, setGroupPosts] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [posting, setPosting] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const [allRes, myRes] = await Promise.all([
        axios.get(`${API}/groups/`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/groups/?my_groups=true`, { headers: getAuthHeaders(), withCredentials: true }),
      ]);
      setGroups(allRes.data);
      setMyGroups(myRes.data);
    } catch { toast.error('Failed to load groups'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim() || !newGroup.description.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/groups/`, newGroup, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Group created!');
      setShowCreateDialog(false);
      setNewGroup({ name: '', description: '', is_private: false });
      fetchGroups();
    } catch { toast.error('Failed to create group'); }
    finally { setCreating(false); }
  };

  const handleJoinGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Joined group!');
      fetchGroups();
      if (selectedGroup?.group_id === groupId) fetchGroupDetail(groupId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/leave`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Left group');
      fetchGroups();
      if (selectedGroup?.group_id === groupId) setSelectedGroup(null);
    } catch { toast.error('Failed to leave group'); }
  };

  const fetchGroupDetail = async (groupId) => {
    setLoadingDetail(true);
    try {
      const [postsRes, membersRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/posts`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/groups/${groupId}/members`, { headers: getAuthHeaders(), withCredentials: true }),
      ]);
      setGroupPosts(postsRes.data);
      setGroupMembers(membersRes.data);
    } catch { toast.error('Failed to load group details'); }
    finally { setLoadingDetail(false); }
  };

  const openGroup = (group) => {
    setSelectedGroup(group);
    fetchGroupDetail(group.group_id);
  };

  const handlePostInGroup = async () => {
    if (!newPostContent.trim() || !selectedGroup) return;
    setPosting(true);
    try {
      await axios.post(`${API}/posts/`, { content: newPostContent, group_id: selectedGroup.group_id }, { headers: getAuthHeaders(), withCredentials: true });
      setNewPostContent('');
      toast.success('Posted!');
      fetchGroupDetail(selectedGroup.group_id);
    } catch { toast.error('Failed to post'); }
    finally { setPosting(false); }
  };

  const isMember = (groupId) => myGroups.some(g => g.group_id === groupId);

  const filteredGroups = groups.filter(g =>
    g.name.toLowerCase().includes(searchQuery.toLowerCase()) || g.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const diff = Date.now() - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Group Detail View
  if (selectedGroup) {
    const memberOfGroup = isMember(selectedGroup.group_id);
    return (
      <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="group-detail-page">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-12">
          <Button variant="ghost" className="mb-4" onClick={() => setSelectedGroup(null)} data-testid="back-to-groups">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Groups
          </Button>

          <Card className="card-default mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {selectedGroup.is_private ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
                    <Badge variant={selectedGroup.is_private ? 'secondary' : 'outline'}>{selectedGroup.is_private ? 'Private' : 'Public'}</Badge>
                  </div>
                  <CardTitle className="text-2xl font-serif">{selectedGroup.name}</CardTitle>
                  <CardDescription className="mt-1">{selectedGroup.description}</CardDescription>
                </div>
                {memberOfGroup ? (
                  <Button variant="outline" size="sm" onClick={() => handleLeaveGroup(selectedGroup.group_id)} data-testid="leave-group-detail">Leave</Button>
                ) : (
                  <Button className="btn-primary" size="sm" onClick={() => handleJoinGroup(selectedGroup.group_id)} data-testid="join-group-detail">Join</Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {selectedGroup.member_count} members</span>
                <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4" /> {groupPosts.length} posts</span>
              </div>
              {/* Member avatars */}
              <div className="flex -space-x-2 mt-3">
                {groupMembers.slice(0, 8).map(m => (
                  <Avatar key={m.user_id} className="h-8 w-8 border-2 border-background">
                    <AvatarImage src={m.picture} />
                    <AvatarFallback className="text-xs">{m.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                ))}
                {groupMembers.length > 8 && <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background">+{groupMembers.length - 8}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Post in group */}
          {memberOfGroup && (
            <Card className="card-default mb-6" data-testid="group-post-card">
              <CardContent className="pt-4">
                <div className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-xs">{user?.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 flex gap-2">
                    <Textarea placeholder="Start a discussion in this group..." value={newPostContent} onChange={e => setNewPostContent(e.target.value)} className="min-h-[60px] resize-none input-default text-sm" data-testid="group-post-input" />
                    <Button size="icon" onClick={handlePostInGroup} disabled={!newPostContent.trim() || posting} className="btn-primary h-10 w-10 flex-shrink-0" data-testid="group-post-submit">
                      {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Group Discussion Board */}
          {loadingDetail ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : groupPosts.length === 0 ? (
            <Card className="card-default text-center py-12">
              <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{memberOfGroup ? 'No posts yet. Start the conversation!' : 'Join this group to see and create posts.'}</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {groupPosts.map(post => (
                <Card key={post.post_id} className="card-default" data-testid={`group-post-${post.post_id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={post.author_picture} />
                        <AvatarFallback className="text-xs">{post.author_name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{post.is_anonymous ? 'Anonymous' : post.author_name}</p>
                          <span className="text-xs text-muted-foreground">{formatDate(post.created_at)}</span>
                        </div>
                        <p className="text-sm mt-1 whitespace-pre-wrap">{post.content}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Groups List View
  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="groups-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">Groups</h1>
            <p className="text-muted-foreground mt-1">Join groups to connect with others on similar immigration journeys</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-group-btn"><Plus className="h-4 w-4 mr-2" /> Create Group</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif text-xl">Create New Group</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Group Name</Label>
                  <Input placeholder="e.g., H1B Visa Seekers" value={newGroup.name} onChange={e => setNewGroup(p => ({...p, name: e.target.value}))} className="input-default" data-testid="group-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea placeholder="What's this group about?" value={newGroup.description} onChange={e => setNewGroup(p => ({...p, description: e.target.value}))} className="input-default min-h-[100px]" data-testid="group-desc-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="group-private" checked={newGroup.is_private} onCheckedChange={c => setNewGroup(p => ({...p, is_private: c}))} data-testid="group-private-toggle" />
                  <Label htmlFor="group-private">Private Group</Label>
                </div>
                <Button onClick={handleCreateGroup} disabled={creating || !newGroup.name.trim() || !newGroup.description.trim()} className="w-full btn-primary" data-testid="submit-group-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Group'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search groups..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 input-default" data-testid="search-groups-input" />
        </div>

        {/* My Groups */}
        {myGroups.length > 0 && (
          <div className="mb-8">
            <h2 className="font-serif text-xl font-semibold mb-4">My Groups</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myGroups.map(group => (
                <Card key={group.group_id} className="card-default cursor-pointer hover:card-active transition-all" onClick={() => openGroup(group)} data-testid={`my-group-${group.group_id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{group.name}</CardTitle>
                        <CardDescription className="line-clamp-2 mt-1">{group.description}</CardDescription>
                      </div>
                      {group.is_private ? <Lock className="h-4 w-4 text-muted-foreground" /> : <Unlock className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4" /> {group.member_count} members</div>
                      <Badge className="bg-primary/10 text-primary">Joined</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Discover */}
        <h2 className="font-serif text-xl font-semibold mb-4">Discover Groups</h2>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : filteredGroups.length === 0 ? (
          <Card className="card-default text-center py-12"><p className="text-muted-foreground">No groups found. Create one to get started!</p></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredGroups.map((group, index) => (
              <Card key={group.group_id} className="card-default animate-slide-up cursor-pointer hover:card-active transition-all" style={{ animationDelay: `${index * 0.05}s` }} onClick={() => openGroup(group)} data-testid={`group-${group.group_id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{group.name}</CardTitle>
                      <CardDescription className="line-clamp-2 mt-1">{group.description}</CardDescription>
                    </div>
                    <Badge variant={group.is_private ? 'secondary' : 'outline'}>{group.is_private ? 'Private' : 'Public'}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4" /> {group.member_count} members</div>
                    {isMember(group.group_id) ? (
                      <Badge className="bg-primary/10 text-primary">Joined</Badge>
                    ) : (
                      <Button size="sm" className="btn-primary" onClick={e => { e.stopPropagation(); handleJoinGroup(group.group_id); }} data-testid={`join-group-${group.group_id}`}>Join</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupsPage;
